import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PlanCuenta } from '../types/planCuenta';
import { PlanCuentaService } from '../services/planCuentaService';

interface PlanCuentaState {
  cuentas: PlanCuenta[];
  loading: boolean;
  error: string | null;
  selectedCuenta: PlanCuenta | null;
  dialogOpen: boolean;

  fetchCuentas: () => Promise<void>;
  saveCuenta: (cuenta: Partial<PlanCuenta>) => Promise<void>;
  deleteCuenta: (id: number) => Promise<void>;
  setSelectedCuenta: (cuenta: PlanCuenta | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const usePlanCuentaStore = create<PlanCuentaState>()(
  devtools(
    (set) => ({
      cuentas: [],
      loading: false,
      error: null,
      selectedCuenta: null,
      dialogOpen: false,

      fetchCuentas: async () => {
        set({ loading: true, error: null }, false, 'planCuenta/fetch/start');
        try {
          const cuentas = await PlanCuentaService.getAll();
          set({ cuentas, loading: false }, false, 'planCuenta/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar cuentas';
          set({ loading: false, error: errorMessage }, false, 'planCuenta/fetch/error');
        }
      },

      saveCuenta: async (cuenta) => {
        set({ loading: true, error: null }, false, 'planCuenta/save/start');
        try {
          await PlanCuentaService.save(cuenta);
          const cuentas = await PlanCuentaService.getAll();
          set({ cuentas, loading: false, dialogOpen: false, selectedCuenta: null }, false, 'planCuenta/save/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al guardar cuenta';
          set({ loading: false, error: errorMessage }, false, 'planCuenta/save/error');
          throw error;
        }
      },

      deleteCuenta: async (id) => {
        set({ loading: true, error: null }, false, 'planCuenta/delete/start');
        try {
          await PlanCuentaService.delete(id);
          const cuentas = await PlanCuentaService.getAll();
          set({ cuentas, loading: false }, false, 'planCuenta/delete/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al eliminar cuenta';
          set({ loading: false, error: errorMessage }, false, 'planCuenta/delete/error');
          throw error;
        }
      },

      setSelectedCuenta: (cuenta) => set({ selectedCuenta: cuenta }, false, 'planCuenta/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'planCuenta/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedCuenta: null }, false, 'planCuenta/closeDialog'),
      clearError: () => set({ error: null }, false, 'planCuenta/clearError'),
    }),
    { name: 'PlanCuentaStore' }
  )
);
